<template>
  <div class="intent-pie-container">
    <div v-if="loading" class="loading-placeholder">
      <span>加载中...</span>
    </div>
    <div v-else-if="hasData" ref="chartRef" class="chart-container"></div>
    <div v-else class="no-data-placeholder">
      <span>暂无数据</span>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getIntentStatistics } from '@/api/dashboard/index'

export default {
  name: 'AdminIntentPieChart',
  props: {
    tenantId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      chartInstance: null,
      loading: false,
      chartData: [],
      colors: [
        '#057FDB', '#FF6B6B', '#FFD93D', '#6BCB77', 
        '#4D96FF', '#9B59B6', '#FF8A5C'
      ]
    }
  },
  computed: {
    hasData() {
      return this.chartData.length > 0 && 
             this.chartData.some(item => item.value > 0)
    }
  },
  mounted() {
    this.initChart()
    this.fetchData()
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    initChart() {
      if (this.$refs.chartRef) {
        this.chartInstance = echarts.init(this.$refs.chartRef)
        window.addEventListener('resize', this.handleResize)
      }
    },
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    },
    async fetchData() {
      if (!this.tenantId) {
        console.warn('tenantId 为空，无法获取问题分类统计数据')
        return
      }
      
      this.loading = true
      try {
        const response = await getIntentStatistics(this.tenantId)
        console.log("getIntentStatistics", response);
        if (response.data.code === 200) {
          this.chartData = response.data.data.map(item => ({
            name: item.intent_name,
            value: item.count,
            code: item.intent_code
          }))
          this.renderChart()
        } else {
          console.error('获取问题分类统计失败:', response.data.message)
        }
      } catch (error) {
        console.error('获取问题分类统计异常:', error)
      } finally {
        this.loading = false
      }
    },
    renderChart() {
      console.log('开始渲染图表，chartInstance:', this.chartInstance)
      console.log('$refs.chartRef:', this.$refs.chartRef)
      
      if (!this.chartInstance) {
        console.warn('chartInstance 不存在，重新初始化')
        this.initChart()
        // 如果仍然不存在，等待下一帧重试
        if (!this.chartInstance) {
          this.$nextTick(() => {
            this.initChart()
            if (this.chartInstance) {
              this.renderChart()
            }
          })
          return
        }
      }
      
      const validData = this.chartData.filter(item => item.value > 0)
      console.log('有效数据:', validData)
      
      if (validData.length === 0) {
        this.chartInstance.clear()
        this.showEmptyState()
        return
      }
      
      // 强制设置图表大小
      this.chartInstance.resize()
      
      const total = validData.reduce((sum, item) => sum + item.value, 0)
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const percent = ((params.value / total) * 100).toFixed(1)
            return `<div style="font-weight:bold;">${params.name}</div>
                    <div>数量：<span style="color:#057FDB;font-weight:bold;">${params.value}</span> 次</div>
                    <div>占比：<span style="color:#FF6B6B;font-weight:bold;">${percent}%</span></div>`
          }
        },
        legend: {
          orient: 'vertical',
          right: '5%',
          top: 'center',
          itemWidth: 14,
          itemHeight: 14,
          textStyle: {
            fontSize: 12,
            color: '#333'
          },
          formatter: (name) => {
            const item = validData.find(d => d.name === name)
            if (item && total > 0) {
              const percent = ((item.value / total) * 100).toFixed(1)
              return `${name}  ${percent}%`
            }
            return name
          }
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['40%', '50%'],
            avoidLabelOverlap: true,
            padAngle: 2,
            itemStyle: {
              borderRadius: 6,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              position: 'outside',
              formatter: (params) => {
                const percent = ((params.value / total) * 100).toFixed(1)
                return `${params.name}\n${percent}%`
              },
              fontSize: 11,
              color: '#666',
              lineHeight: 18
            },
            labelLine: {
              length: 12,
              length2: 8,
              smooth: true
            },
            emphasis: {
              scaleSize: 8,
              label: {
                show: true,
                fontSize: 13,
                fontWeight: 'bold'
              }
            },
            data: validData,
            color: this.colors
          }
        ]
      }
      
      console.log('设置 ECharts 选项:', option)
      this.chartInstance.setOption(option, true) // 第二个参数 true 表示不合并，完全替换
      
      // 强制重绘
      setTimeout(() => {
        if (this.chartInstance) {
          this.chartInstance.resize()
        }
      }, 100)
    },
    refresh() {
      this.fetchData()
    }
  },
  watch: {
    tenantId: {
      handler(newVal) {
        if (newVal) {
          this.fetchData()
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.intent-pie-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.chart-container {
  width: 100%;
  height: 260px;
}

.loading-placeholder,
.no-data-placeholder {
  width: 100%;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
  background: #fafafa;
  border-radius: 4px;
}

.loading-placeholder span::before {
  content: '';
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: 2px solid #e0e0e0;
  border-top-color: #057FDB;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>